import { formatNumber } from "util/format"

import { TextProps } from "@chakra-ui/react"
import { getLeastSignificantDigit } from "@perp/sdk-curie"
import Big from "big.js"
import React, { useEffect, useState } from "react"
import AnimatedNumber from "react-animated-number"
import { usePrevious } from "react-use"

import { MotionBox } from "./Motion"
import { Stats } from "./Stats"

/**
 * A custom Stats with animated number support.
 * This component customized numberComponent in the OG stats component
 * and added some frequently-used props.
 *
 * Meant to be used on some (not all) key stats.
 * Based on: https://github.com/ameyms/react-animated-number
 *
 * Special props:
 * - dollarSign: whether to add a dollar sign to the number
 */
interface LiveStatsProps {
    number: number
    label: string
    size?: string
    showTooltip?: boolean
    labelColor?: string
    labelProps?: TextProps
    variant?: string
    dollarSign?: boolean
    helper?: string
    showPnlColor?: boolean
    precision?: number
    suffix?: string
    numberProps?: TextProps
}

export const LiveStats = ({
    number,
    label,
    size,
    variant,
    labelColor,
    helper,
    showTooltip,
    dollarSign,
    showPnlColor,
    suffix = "",
    precision = 5,
    labelProps,
    numberProps,
}: LiveStatsProps) => {
    // set current and prev number to compare for change
    const [currentNumber, setCurrentNumber] = useState<number>(number)
    const prevNumber = usePrevious(currentNumber)
    // change state
    const [change, setChange] = useState<string>("")
    useEffect(() => {
        setCurrentNumber(number)

        // detect change in number and briefly show change direction
        if (prevNumber !== undefined && currentNumber > prevNumber) {
            setTimeout(() => setChange("up"), 0)
            setTimeout(() => setChange(""), 1000)
        }
        if (prevNumber !== undefined && currentNumber < prevNumber) {
            setTimeout(() => setChange("down"), 0)
            setTimeout(() => setChange(""), 1000)
        }
    }, [number, change, currentNumber, prevNumber])

    const renderColor = (number: number) => {
        if (showPnlColor) {
            if (number >= 0) {
                return "rgba(64, 200, 127, 1)"
            } else if (number < 0) {
                return "rgba(234,98,98,1)"
            } else {
                return undefined
            }
        }
    }

    const renderPnlSign = (showPnlColor?: boolean) => {
        if (number >= 0 && showPnlColor) {
            return "+"
        } else {
            return ""
        }
    }
    return (
        <Stats
            label={label}
            labelColor={labelColor}
            labelProps={labelProps}
            numberProps={numberProps}
            size={size}
            variant={variant}
            helper={helper}
            showTooltip={showTooltip}
            numberComponent={
                <>
                    <MotionBox
                        initial={{ y: 0 }}
                        animate={{
                            y: change === "up" ? [-6, 0] : change === "down" ? [6, 0] : 0,
                            color:
                                change === "up"
                                    ? "rgba(64,200,127,1)"
                                    : change === "down"
                                    ? "rgba(234,98,98,1)"
                                    : renderColor(number),
                        }}
                    >
                        <AnimatedNumber
                            stepPrecision={getLeastSignificantDigit(number, precision)}
                            value={number}
                            initialValue={0}
                            duration={1000}
                            formatValue={(number: number) => {
                                function renderDollarSign(dollarSign?: boolean) {
                                    if (dollarSign && number >= 0) {
                                        return "$"
                                    } else if (dollarSign && number < 0) {
                                        return "-$"
                                    } else {
                                        return ""
                                    }
                                }

                                if (number >= 0) {
                                    // is positive and require dollar sign, use $
                                    return (
                                        renderPnlSign(showPnlColor) +
                                        renderDollarSign(dollarSign) +
                                        formatNumber(Big(number), getLeastSignificantDigit(number, precision)) +
                                        suffix
                                    )
                                } else if (number < 0 && dollarSign) {
                                    // if negative, use -$
                                    return (
                                        renderPnlSign(showPnlColor) +
                                        renderDollarSign(dollarSign) +
                                        formatNumber(Big(number).abs(), getLeastSignificantDigit(number, 0)) +
                                        suffix
                                    )
                                } else if (number < 0) {
                                    // if negative, use -$
                                    return (
                                        renderPnlSign(showPnlColor) +
                                        formatNumber(Big(number), getLeastSignificantDigit(number, 0)) +
                                        suffix
                                    )
                                } else {
                                    return "—"
                                }
                            }}
                        />
                    </MotionBox>
                    {/* show arrow symbol if it's changing */}
                    <ChangeSign change={change} />
                </>
            }
        />
    )
}

const ChangeSign = ({ change }: { change: string }) => {
    return (
        <MotionBox
            fontSize={"0.55em"}
            as="span"
            w="12px"
            color={change === "up" ? "green.400" : "red.400"}
            animate={{
                opacity: change !== "" ? [1, 0] : 0,
                y: change === "up" ? [-6, 0] : change === "down" ? [6, 0] : 0,
            }}
            initial={{ opacity: 1, y: 0 }}
        >
            {change === "up" ? "▲" : change === "down" ? "▼" : " "}
        </MotionBox>
    )
}
